import { useTrans } from '../../../services/i18n';
import Typography from '../../../components/common/Typography';
import { Grid } from '@material-ui/core';
import { Button } from '../../../components/common/Buttons';
import CodeInput from '../../../components/common/Inputs/CodeInput';
import { Divider } from '../../../components/common/Divider';
import { useState, useEffect } from 'react';
import { useApplication } from '../../../services/application';
import { useAlertContext } from '../../../contexts/AlertContext';
import { AmplifyAuth } from '../../../services/amplifyAuth';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import makeStyles from '@material-ui/core/styles/makeStyles';

const AUTH_SMS_MFA = 'SMS_MFA';

const useStyles = makeStyles(() => ({
  phoneInputWrapper: {
    position: 'relative',
    background: '#f1f3f9',
    borderRadius: '4px',
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    padding: '0 150px 0 18px',
    boxSizing: 'border-box',
  },
  phoneInput: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Lexend',
    '& .PhoneInputCountry': {
      display: 'flex',
      alignItems: 'center',
      marginRight: '12px',
      flexShrink: 0,
    },
    '& .PhoneInputCountryIcon': {
      width: '2.5rem',
      height: '1.75rem',
      boxShadow: 'none',
    },
    '& .PhoneInputCountryIconImg': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    '& .PhoneInputCountrySelectArrow': {
      opacity: 0.6,
      marginLeft: '8px',
    },
    '& .PhoneInputInput': {
      border: 'none',
      outline: 'none',
      boxShadow: 'none',
      background: 'transparent',
      padding: 0,
      margin: 0,
      width: '100%',
      fontFamily: 'Lexend',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111111',
    },
    '& .PhoneInputInput:focus': {
      border: 'none',
      outline: 'none',
      boxShadow: 'none',
    },
  },
  sendCodeButton: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 118,
    height: 40,
  },
}));

const SmsFa = ({ userPhone = '', updateMFA, updatePhone, setSuccess }) => {
  const application = useApplication();
  const { trans } = useTrans();
  const styles = useStyles();
  const [phone, setPhone] = useState('');
  const [pendingSendCode, setSendCodePending] = useState(false);
  const [pendingVerifyCode, setPendingVerifyCode] = useState(false);
  const { errorAlert, successAlert } = useAlertContext();
  const [code, setCode] = useState('');
  const [sentCode, setSentCode] = useState(false);

  const handleSuccess = () => {
    setSuccess(true);
    updateMFA(AUTH_SMS_MFA);
  };

  const enableSmsMfa = async () => {
    const currentUser = await AmplifyAuth.currentAuthenticatedUser({
      bypassCache: true,
    });

    await AmplifyAuth.setPreferredMFA(currentUser, 'SMS');
    handleSuccess();
  };

  const handleSetPhone = async () => {
    setSendCodePending(true);

    try {
      const currentUser = await AmplifyAuth.currentAuthenticatedUser({
        bypassCache: true,
      });

      const currentPhone = currentUser.attributes?.phone_number;
      const phoneVerified =
        currentUser.attributes?.phone_number_verified === true ||
        currentUser.attributes?.phone_number_verified === 'true';

      if (!sentCode && currentPhone === phone && phoneVerified) {
        await enableSmsMfa();
        return;
      }

      if (!sentCode) {
        await AmplifyAuth.updateUserAttributes(currentUser, {
          phone_number: phone,
        });

        await AmplifyAuth.verifyCurrentUserAttribute('phone_number');

        updatePhone(phone);
        setSentCode(true);
        successAlert(trans('new-code-has-been-sent'));
        return;
      }

      await AmplifyAuth.verifyCurrentUserAttribute('phone_number');
      successAlert(trans('new-code-has-been-sent'));
    } catch (e) {
      errorAlert(e.message);
    } finally {
      setSendCodePending(false);
    }
  };

  const setMfa = async () => {
    setPendingVerifyCode(true);

    try {
      await AmplifyAuth.verifyCurrentUserAttributeSubmit('phone_number', code);

      const res = await application.call('admin.profile.setPhone', { phone });

      updatePhone(res?.phone || phone);

      const currentUser = await AmplifyAuth.currentAuthenticatedUser({
        bypassCache: true,
      });

      await AmplifyAuth.setPreferredMFA(currentUser, 'SMS');

      handleSuccess();
    } catch (e) {
      errorAlert(e.message);
    } finally {
      setPendingVerifyCode(false);
    }
  };

  useEffect(() => {
    if (userPhone) {
      setPhone(userPhone);
    }
  }, [userPhone]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography gutter="label" variant="h6" component="p" weight="bold" color="label">
            {trans('confirm-your-phone')}
          </Typography>

          <div className={styles.phoneInputWrapper}>
            <PhoneInput
              defaultCountry="US"
              international
              placeholder="Enter phone number"
              value={phone}
              onChange={setPhone}
              className={styles.phoneInput}
            />

            <Button
              variant="outlined"
              color="primary"
              size="small"
              className={styles.sendCodeButton}
              pending={pendingSendCode}
              onClick={handleSetPhone}
              disabled={!phone}
            >
              {!sentCode ? trans('send-code') : trans('resend-code')}
            </Button>
          </div>
        </Grid>

        {sentCode && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" component="p" weight="bold" color="label">
                {trans('step-verification')}
              </Typography>

              <Typography variant="h6" component="p" color="label">
                Please verify the CODE sent to your phone registered and enter below
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography gutter="label" variant="h6" component="p" weight="bold" color="label">
                {trans('enter-code')}
              </Typography>

              <CodeInput fields={6} type="number" onChange={(e) => setCode(e)} />
            </Grid>
          </>
        )}
      </Grid>

      {sentCode && (
        <>
          <Divider light spacing={4} />

          <div style={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={setMfa}
              disabled={!phone || code.length < 6}
              pending={pendingVerifyCode}
            >
              {trans('verify')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default SmsFa;